import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Form } from "@/components/ui/form";
import { CheckCircle, ArrowLeft, Loader2, Calendar, User, Mail, Phone, GraduationCap, FileText, Info } from "lucide-react";

interface FormData {
  studentFirstName: string;
  studentFirstSurname: string;
  studentSecondSurname?: string;
  studentClass: string;
  parentEmail: string;
  parentPhone: string;
  selectedActivities: string[];
}

interface ActivityConfig {
  id: string;
  name: string;
  billingType: 'per_session' | 'monthly';
  selectedDays: number[];
  requiresDaySelection: boolean;
}

interface SummaryStepProps {
  form: UseFormReturn<FormData>;
  formData: FormData;
  activityConfigs: ActivityConfig[];
  onBack: () => void;
  onSubmit: (data: FormData) => void;
  isLoading?: boolean;
}

const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

export function SummaryStep({ 
  form, 
  formData, 
  activityConfigs, 
  onBack, 
  onSubmit, 
  isLoading = false 
}: SummaryStepProps) {
  const handleSubmit = (data: FormData) => {
    onSubmit(data);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg">
          <FileText className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900">Resumen de Inscripción</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Revisa toda la información antes de confirmar tu inscripción
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-start">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="h-12 px-6 rounded-xl border-gray-200 hover:border-gray-300 transition-all"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver a actividades
        </Button>
      </div>

      {/* Student Data Summary */}
      <Card className="shadow-xl border-0 bg-gradient-to-br from-white to-blue-50/30">
        <CardHeader className="pb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-2xl text-gray-900">Datos del Estudiante</CardTitle>
              <CardDescription className="text-gray-600">
                Información personal verificada
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <User className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Nombre completo</span>
                <p className="font-semibold text-gray-900">{`${formData.studentFirstName} ${formData.studentFirstSurname}${formData.studentSecondSurname ? ' ' + formData.studentSecondSurname : ''}`}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Clase</span>
                <p className="font-semibold text-gray-900">{formData.studentClass}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Email de contacto</span>
                <p className="font-semibold text-gray-900">{formData.parentEmail}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Phone className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Teléfono</span>
                <p className="font-semibold text-gray-900">{formData.parentPhone}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activities Summary */}
      <Card className="shadow-xl border-0 bg-gradient-to-br from-white to-green-50/30">
        <CardHeader className="pb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-2xl text-gray-900">
                Actividades Seleccionadas 
                <span className="ml-2 text-lg text-green-600">({activityConfigs.length})</span>
              </CardTitle>
              <CardDescription className="text-gray-600">
                Lista de actividades en las que se inscribirá el estudiante
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {activityConfigs.map((config, index) => (
            <div key={config.id} className="bg-white/70 border border-green-100 rounded-xl p-5 space-y-3 transition-all hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{config.name}</h4>
                  </div>
                </div>
                <Badge 
                  variant="secondary" 
                  className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200"
                >
                  {config.billingType === 'per_session' ? 'Uso días sueltos' : 'Uso mensual'}
                </Badge>
              </div>
              
              {config.requiresDaySelection && config.selectedDays.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                  <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-blue-900">Días seleccionados:</span>
                  <div className="flex gap-1 flex-wrap">
                    {config.selectedDays.map((day) => (
                      <Badge key={day} variant="outline" className="text-xs bg-white border-blue-200 text-blue-700">
                        {dayNames[day]}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Important Information */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-orange-200 rounded-2xl p-6 shadow-lg">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Info className="w-6 h-6 text-white" />
          </div>
          <div className="space-y-3">
            <h4 className="text-xl font-semibold text-orange-900">Información Importante</h4>
            <ul className="text-sm text-orange-800 space-y-2 leading-relaxed">
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Recibirás una confirmación por email una vez completada la inscripción</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>En caso de necesitar hacer cambios ponerse en contacto con el centro</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Submit Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="flex justify-center pt-4">
            <Button 
              type="submit" 
              disabled={isLoading}
              className="h-16 px-12 text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-3 h-6 w-6 animate-spin" />
                  Procesando inscripción...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-3 h-6 w-6" />
                  Confirmar Inscripción
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}